import {createTheme} from "@mui/material/styles";
import {ThemeProvider} from "@mui/system";
import React, { createContext, useContext, useState } from "react";

interface ThemeContextType {
  darkMode: boolean;
  toggleDarkMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProviderWrapper: React.FC = ({ children }) => {
  const [darkMode, setDarkMode] = useState(true);

  const darkTheme = createTheme({
    palette: {
      mode: "dark",
      primary: {
        main: "#cb8af9",
        light: "#e0b9fa",
        dark: "#B557F7",
        contrastText: "#000000",
      },
      secondary: {
        main: "#6BD9D5",
        light: "#aae7e6",
        dark: "#009e93",
        contrastText: "#000000",
      },
      info: {
        main: "#e6f0ff",
        light: "#eef8ff",
        dark: "#cad4ec",
        contrastText: "#212b3a",
      },
      warning: {
        main: "#FFE081",
        light: "#fff8e1",
        dark: "#ffb200",
        contrastText: "#212b3a",
      },
      error: {
        main: "#fa8996",
        light: "#ffe8ee",
        dark: "#f3002c",
        contrastText: "#212b3a",
      },
      // background: {
      //   default: "#232D3D",
      //   paper: "#0B1627",
      // },
    },
    typography: {
      fontFamily: "cera-pro",
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 1280,
        md: 1600,
        lg: 2200,
        xl: 6000,
      },
    },
    components: {
      MuiFilledInput: {
        defaultProps: {
          disableUnderline: true,
        },
        styleOverrides: {
          root: {
            borderRadius: "4px",
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: "6px",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: "6px",
          },
        },
      },
    },
  });

  const lightTheme = createTheme({
    palette: {
      mode: "light",
      primary: {
        main: "#B557F7",
        light: "#cb8af9",
        dark: "#8841B9",
        contrastText: "#ffffff",
      },
      secondary: {
        main: "#EA673B",
        light: "#F5B49F",
        dark: "#b54c29",
        contrastText: "#ffffff",
      },
      info: {
        main: "#1C2636",
        light: "#404956",
        dark: "#0B1627",
        contrastText: "#ffffff",
      },
      warning: {
        main: "#ffb200",
        light: "#FFE081",
        dark: "#ff6e00",
        contrastText: "#212b3a",
      },
      error: {
        main: "#f3002c",
        light: "#fa8996",
        dark: "#c30013",
        contrastText: "#ffffff",
      },
      background: {
        default: "#F6F8F8",
        paper: "#DBDBDC",
      },
      action: {
        active: '#212b3a'
      }
    },
    typography: {
      fontFamily: "cera-pro",
      allVariants: {
        color: "#212b3a",
      },
    },
    components: {
      MuiFilledInput: {
        defaultProps: {
          disableUnderline: true,
        },
        styleOverrides: {
          root: {
            borderRadius: "4px",
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: "6px",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: "6px",
          },
        },
      },
    },
  });

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const theme = darkMode ? darkTheme : lightTheme;

  return (
    <ThemeProvider theme={theme}>
      <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
        {children}
      </ThemeContext.Provider>
    </ThemeProvider>
  );
};


export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
